import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { Button } from 'epoch-ui/components/Button/button';
import {
  Form,
  FormItem,
  FormField,
  FormLabel,
  FormMessage,
  SwitchField,
  CheckboxField
} from 'epoch-ui/components/Form';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalFooter,
  ModalDescription
} from 'epoch-ui/components/Modal/modal';
import { TimeOffsetField, TimeOffsetConfig } from '@epoch-components/TimeOffsetField';
import { TimeOffsetUnit, TimeOffsetRelation } from 'constants/enums';
import { useEditableEvent } from 'pages/dashboard/overview/events/editor/hooks/useEditableEvent';
import { EventType } from 'types/event.types';

import { SelfCheckinModalFormValues, getSelfCheckinModalSchema } from './selfCheckInModal.schema';

const timeOffsetConfigPresetsForEventType = (
  eventType: EventType
): Record<'eventBegins' | 'eventEnds', TimeOffsetConfig> => {
  const eventLabel = eventType === EventType.MULTI_SESSIONS ? 'session' : 'event';
  const units = [
    {
      value: TimeOffsetUnit.MINUTES,
      label: 'minutes'
    },
    {
      value: TimeOffsetUnit.HOURS,
      label: 'hours'
    },
    {
      value: TimeOffsetUnit.DAYS,
      label: 'days'
    }
  ];
  return {
    eventBegins: {
      units,
      relations: [
        {
          value: TimeOffsetRelation.BEFORE,
          label: `before the ${eventLabel} begins`
        },
        {
          value: TimeOffsetRelation.AFTER,
          label: `after the ${eventLabel} begins`
        }
      ]
    },
    eventEnds: {
      units,
      relations: [
        {
          value: TimeOffsetRelation.BEFORE,
          label: `before the ${eventLabel} ends`
        },
        {
          value: TimeOffsetRelation.AFTER,
          label: `after the ${eventLabel} ends`
        }
      ]
    }
  };
};

interface EditSelfCheckinFormModalProps extends React.ComponentProps<typeof Modal> {
  onSubmit: (values: SelfCheckinModalFormValues) => void;
}

const EditSelfCheckinFormModal: React.FC<EditSelfCheckinFormModalProps> = ({
  onOpenChange,
  onSubmit,
  ...props
}) => {
  const event = useEditableEvent();
  const eventType = event.eventType ?? EventType.SINGLE;
  const timeOffsetConfigPresets = React.useMemo(
    () => timeOffsetConfigPresetsForEventType(eventType),
    [eventType]
  );

  const initialValues = React.useMemo(
    () => ({
      allowUserCheckin: event.allowUserCheckin,
      sendNotificationSettings: {
        email: event.sendUserCheckinEmail,
        slack: event.sendUserCheckinSlack
      },
      schedule: {
        quantity: Number(event.selfCheckinSchedule?.quantity) || 10,
        unit: (
          (event.selfCheckinSchedule?.unit as TimeOffsetUnit) || TimeOffsetUnit.MINUTES
        ).toLowerCase(),
        relation:
          event.selfCheckinSchedule?.beforeEventBegins ?? true
            ? TimeOffsetRelation.BEFORE
            : TimeOffsetRelation.AFTER
      }
    }),
    [
      event.allowUserCheckin,
      event.sendUserCheckinEmail,
      event.sendUserCheckinSlack,
      event.selfCheckinSchedule
    ]
  );

  const form = useForm<SelfCheckinModalFormValues>({
    resolver: zodResolver(getSelfCheckinModalSchema(event)),
    defaultValues: initialValues,
    mode: 'onChange'
  });

  const allowUserCheckin = form.watch('allowUserCheckin');

  const handleOpenChange = (open: boolean): void => {
    if (!open) {
      form.reset(initialValues);
    }
    onOpenChange?.(open);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.stopPropagation();
    form.handleSubmit(onSubmit, (errors) => {
      console.error(errors);
    })(e);
  };

  return (
    <Modal {...props} onOpenChange={handleOpenChange}>
      <Form {...form}>
        <ModalContent size="sm">
          <form onSubmit={handleSubmit}>
            <ModalHeader className="tw-mb-2">
              <ModalTitle className="tw-text-2xl tw-font-medium">Self check-in</ModalTitle>
            </ModalHeader>
            <ModalDescription className="tw-mb-5 tw-mt-0 tw-text-sm tw-text-muted-foreground">
              You can use self check-in to allow guests to mark themselves as attended to
              {eventType === EventType.MULTI_SESSIONS ? ' the session. ' : ' the event. '}
              Registered guests will be notified to check in for
              {eventType === EventType.MULTI_SESSIONS ? ' each session.' : ' the event.'}
            </ModalDescription>

            <div>
              <SwitchField
                name="allowUserCheckin"
                label="Use self check-in"
                aria-label="Use self check-in toggle"
                className="tw-mb-4 tw-mt-0 tw-flex-row-reverse tw-justify-between"
                disabled={!event.isSelfCheckinEditable}
                onCheckedChange={(isChecked) => {
                  form.reset(
                    (prev) => ({
                      ...prev,
                      allowUserCheckin: isChecked,
                      sendNotificationSettings: { email: true, slack: true }
                    }),
                    { keepDefaultValues: true }
                  );
                }}
              />

              {allowUserCheckin && (
                <div className="tw-flex tw-w-full tw-flex-col tw-gap-4">
                  <TimeOffsetField
                    name="schedule"
                    label="When do you want to send the check-in notifications?"
                    disabled={!event.isSelfCheckinEditable}
                    config={timeOffsetConfigPresets.eventBegins}
                  />
                  <FormField
                    control={form.control}
                    name="sendNotificationSettings"
                    disabled={!event.isSelfCheckinEditable}
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel className="tw-mb-3">How do you want to notify guests?</FormLabel>

                        <CheckboxField
                          label="Email"
                          error={!!fieldState.error}
                          checked={field.value.email}
                          disabled={field.disabled}
                          className="tw-mt-0"
                          onCheckedChange={(isChecked) => {
                            field.onChange({
                              ...field.value,
                              email: isChecked
                            });
                          }}
                        />
                        <CheckboxField
                          label="Slack"
                          error={!!fieldState.error}
                          checked={field.value.slack}
                          disabled={field.disabled}
                          className="tw-mt-2"
                          onCheckedChange={(isChecked) => {
                            field.onChange({
                              ...field.value,
                              slack: isChecked
                            });
                          }}
                        />
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}
            </div>

            <ModalFooter className="tw-mt-5">
              <Button
                size="lg"
                className="tw-w-full tw-rounded-lg"
                type="submit"
                variant="primary"
                loading={form.formState.isSubmitting}
                disabled={
                  !form.formState.isDirty ||
                  !event.isSelfCheckinEditable ||
                  form.formState.isSubmitting
                }
              >
                Save
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Form>
    </Modal>
  );
};

export { EditSelfCheckinFormModal };
